import React, {
  useCallback, useEffect, useMemo,
} from 'react';
import * as Styles from './NewHomeLayoutHandler.styles';
import HomeBannerCreateWithAi from '@/media/home/home_banner_create_with_ai_pt_br.png';
import useLocale from '@/hooks/useLocale';
import {
  Button, Carousel, IconArrow, Skeleton,
} from 'gatorcomponents';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { BLOG_URL, HELP_CENTER_URL } from '@/config/urls/supportUrls';
import { useAgentsFactory } from '@/hooks/agentsFactory';
import {
  emailsActions, linkNaBioActions, productsActions, sitesActions,
} from '@/redux/modules';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { PORTFOLIO_AGENT_FACTORY_NEW_ONBOARDING } from '@/config/GrowthBook/constants';
import { Services } from '../Services';
import { EmptyProductsV2 } from '../EmptyProductsV2';
import { SERVICES_IDS } from '../Services/Services.enum';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { N8N_URL } from '@/config/urls/n8nUrls';

const NewHomeLayoutHandler = ({
  enabledAiAgents, activeHostings, isEmptyProducts,
}) => {
  const enableAgentFactoryNewOnboarding = useFeatureIsOn(PORTFOLIO_AGENT_FACTORY_NEW_ONBOARDING);

  const history = useHistory();
  const dispatch = useDispatch();
  const { openAgentsFactory } = useAgentsFactory();
  const { routes: routesLocale, home: homeLocale } = useLocale();

  const { owned: ownedProducts, loading: productsLoading } = useSelector(state => state.products);
  const { profiles: linkNaBioProfiles, loading: linkNaBioLoading } = useSelector(state => state.linkNaBio);
  const { loadingProEmails: emailsLoading } = useSelector(state => state.emails);
  const {
    list: sitesList,
    agentsFactory,
    loading: sitesLoading,
    domainsWithSitesCreated,
    loadingDomainsWithSitesCreated,
  } = useSelector(state => state.sites);
  const { features } = useSelector(state => state.summary);
  const agentsFactoryLoading = agentsFactory?.loading;

  const hasSitesCreated = useMemo(() => domainsWithSitesCreated?.length, [domainsWithSitesCreated?.length]);
  const hasHosting = useMemo(() => ownedProducts?.some(product => ['shared', 'vps', 'dedicated'].includes(product.category) && !!product.has_product), [ownedProducts]);
  const hasDomain = useMemo(() => ownedProducts?.some(product => product.category === 'domain' && !!product.has_product), [ownedProducts]);
  const hasSites = useMemo(() => !!sitesList?.length, [sitesList]);
  const hasEmail = useMemo(() => ownedProducts?.some(product => product.category === 'proemail' && !!product.has_product), [ownedProducts]);
  const hasLinkNaBio = useMemo(() => !!linkNaBioProfiles?.length, [linkNaBioProfiles]);
  const hasAgentActive = useMemo(() => !!agentsFactory?.account && !!features?.agent_factory, [agentsFactory?.account, features]);
  const hasAgentReady = useMemo(() => !!agentsFactory?.account && !!features?.agent_factory, [agentsFactory?.account, features]);

  const loadingData = useMemo(() => loadingDomainsWithSitesCreated
    || sitesLoading
    || productsLoading
    || linkNaBioLoading
    || emailsLoading
    || agentsFactoryLoading, [
    loadingDomainsWithSitesCreated,
    sitesLoading,
    productsLoading,
    linkNaBioLoading,
    emailsLoading,
    agentsFactoryLoading,
  ]);

  const hasActiveProducts = useMemo(() => (hasHosting || hasDomain || hasSites || hasEmail || hasLinkNaBio || hasAgentActive), [hasHosting, hasDomain, hasSites, hasEmail, hasLinkNaBio, hasAgentActive]);
  const hasReadyProducts = useMemo(() => hasHosting, [hasHosting]);

  const handleRedirect = useCallback(({ id }) => {
    const rules = {
      courses: () => history.push(`${routesLocale?.courses}`),
      blog: () => window.open(BLOG_URL, '_blank'),
      help: () => window.open(HELP_CENTER_URL, '_blank'),
      hosting: () => history.push(`${routesLocale?.products}`),
      domain: () => history.push(`${routesLocale?.domains}`),
      site: () => history.push(`${routesLocale?.sitesPage}`),
      email: () => history.push(`${routesLocale?.emailsList}`),
      linkInBio: () => history.push(`${routesLocale?.linkNaBio}`),
      createAgent: () => openAgentsFactory('create'),
      marketing: () => history.push(`${routesLocale?.marketing}`),
      buyDomain: () => history.push(`${routesLocale?.domains}${routesLocale?.buyDomain}`),
    };

    return rules[id] ? rules[id]() : null;
  }, [history, routesLocale, openAgentsFactory]);

  const handleCreateSiteWithAi = () => {
    history.push(`${routesLocale.sitesPage}${routesLocale.sitesCreate}`);
  };

  const newHomeLayoutRequests = useCallback(() => {
    dispatch(sitesActions.domains.requestDomainsWithSites());
    dispatch(productsActions.products.request({ history: null }));
    dispatch(linkNaBioActions.profile.get.request());
    dispatch(emailsActions.emails.request());
    dispatch(sitesActions.sites.request());
    enableAgentFactoryNewOnboarding && dispatch(sitesActions.agentsFactory.account.request());
  }, [dispatch, enableAgentFactoryNewOnboarding]);

  useEffect(() => {
    newHomeLayoutRequests();
  }, [newHomeLayoutRequests]);

  const readyListItems = useMemo(() => {
    const items = [];

    const createItem = item => (
      <Styles.ColumnCarouselItem key={item?.id}>
        <Styles.ColumnCarouselItemTitleRow>
          <Styles.CarouselItemIconWrapper>{item?.icon}</Styles.CarouselItemIconWrapper>
          <Styles.CarouselItemTitle>{item?.title}</Styles.CarouselItemTitle>
        </Styles.ColumnCarouselItemTitleRow>

        <Styles.CarouselItemDescription>
          {item?.description}
        </Styles.CarouselItemDescription>

        <Styles.CarouselCtaWrapper>
          <Button
            variant="secondary"
            label={item?.cta}
            onClick={() => handleRedirect({ id: item?.id })}
            iconRight={<IconArrow direction="right" color="primaryBlue" />}
            size="medium2"
          />
        </Styles.CarouselCtaWrapper>
      </Styles.ColumnCarouselItem>
    );

    !hasHosting && items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.hosting),
    );

    !hasDomain && items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.domain),
    );

    !hasSites && items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.site),
    );

    !hasEmail && items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.email),
    );

    !hasLinkNaBio && items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.linkInBio),
    );

    hasAgentReady && items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.createAgent),
    );

    items.push(
      createItem(homeLocale?.v2?.readyProductsCarousel.marketing),
    );

    return items;
  }, [
    handleRedirect,
    homeLocale,
    hasAgentReady,
    hasDomain,
    hasHosting,
    hasSites,
    hasEmail,
    hasLinkNaBio,
  ]);

  const activeListItems = useMemo(() => {
    const items = [];

    const createItem = item => (
      <Styles.CarouselItem key={item?.id} $mobileFlexWrap id={`carouselItem${item?.id}`} onClick={() => handleRedirect({ id: item?.id })}>
        <Styles.CarouselRowItemTitle>
          <Styles.CarouselItemIconWrapper>{item?.icon}</Styles.CarouselItemIconWrapper>
          <Styles.CarouselItemTitle>{item?.title}</Styles.CarouselItemTitle>
        </Styles.CarouselRowItemTitle>
        <Styles.CarouselAccessButton>
          <Button
            variant="text"
            label="acessar"
            iconRight={<IconArrow direction="right" color="primaryBlue" />}
          />
        </Styles.CarouselAccessButton>
      </Styles.CarouselItem>
    );

    hasHosting && items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.hosting),
    );

    hasDomain && items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.domain),
    );

    hasSites && items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.site),
    );

    hasEmail && items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.email),
    );

    hasLinkNaBio && items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.linkInBio),
    );

    hasAgentActive && items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.createAgent),
    );

    items.push(
      createItem(homeLocale?.v2?.activeProductsCarousel.marketing),
    );

    return items;
  }, [
    handleRedirect,
    homeLocale,
    hasAgentActive,
    hasDomain,
    hasHosting,
    hasSites,
    hasEmail,
    hasLinkNaBio,
  ]);

  const servicesProps = useCallback(() => {
    const cardsActions = {
      [SERVICES_IDS.ADS]: {
        onClick: () => {
          history.push(routesLocale.marketing);
        },
      },
      [SERVICES_IDS.ADS_FULL]: {
        onClick: () => {
          history.push(routesLocale.marketing);
        },
      },
      [SERVICES_IDS.AI_AGENTS]: {
        onClick: () => {
          openAgentsFactory('create');
        },
      },
      [SERVICES_IDS.VPS_N8N]: {
        onClick: () => {
          const hasVpsN8n = activeHostings && activeHostings.some(
            product => HgProducts.VPS_N8N_ALL.includes(product.package_id),
          );

          if (hasVpsN8n) {
            history.push(routesLocale.products);
          } else {
            window.open(N8N_URL, '_blank');
          }
        },
      },
      [SERVICES_IDS.EMAIL]: {
        onClick: () => {
          history.push(routesLocale.emails);
        },
      },
    };

    return {
      agentFactoryAvailable: enabledAiAgents && features?.agent_factory,
      cardsActions,
    };
  }, [history, routesLocale, openAgentsFactory, enabledAiAgents, activeHostings, features]);

  const emptyProductsProps = useCallback(() => ({
    locale: homeLocale.v2.emptyProducts,
  }), [homeLocale]);

  if (loadingData) {
    return (
      <>
        <Styles.WhiteCard $loading data-testid="loadingState">
          <Styles.SkeletonRow>
            <Skeleton width="100%" height="20px" />
          </Styles.SkeletonRow>

          <Styles.SkeletonRow>
            <Skeleton width="100%" height="400px" />
          </Styles.SkeletonRow>
        </Styles.WhiteCard>

        <Styles.WhiteCard $loading>
          <Styles.SkeletonRow>
            <Skeleton width="100%" height="20px" />
          </Styles.SkeletonRow>

          <Styles.SkeletonInline>
            <Styles.SkeletonRow>
              <Skeleton width="100%" height="400px" />
            </Styles.SkeletonRow>

            <Styles.SkeletonRow>
              <Skeleton width="100%" height="400px" />
            </Styles.SkeletonRow>
          </Styles.SkeletonInline>
        </Styles.WhiteCard>

        <Styles.WhiteCard $loading>
          <Styles.SkeletonRow>
            <Skeleton width="100%" height="20px" />
          </Styles.SkeletonRow>

          <Styles.SkeletonRow>
            <Skeleton width="100%" height="300px" />
          </Styles.SkeletonRow>
        </Styles.WhiteCard>
      </>
    );
  }

  return (
    <Styles.Wrapper data-testid="home-layout-handler">
      {hasReadyProducts && (
        <Styles.WhiteCard data-testid="home-ready-products-carousel">
          <Carousel
            title={homeLocale.v2.readyProductsCarousel.title}
            description={homeLocale.v2.readyProductsCarousel.description}
            list={readyListItems || []}
            withPadding={false}
          />
        </Styles.WhiteCard>
      )}

      {hasActiveProducts && (
        <Styles.WhiteCard data-testid="home-active-products-carousel">
          <Carousel
            title={homeLocale.v2.activeProductsCarousel.title}
            list={activeListItems || []}
            withPadding={false}
          />
        </Styles.WhiteCard>
      )}

      <Styles.WhiteCard data-testid="home-create-site-with-ai-banner">
        <Styles.WhiteCardHeader>
          <Styles.Title>{homeLocale.v2.banner.title}</Styles.Title>
        </Styles.WhiteCardHeader>
        <Styles.WhiteCardContent>
          {(!hasSitesCreated) && (
            <Styles.InnerCard>
              <Styles.InnerCardColumn>
                <Styles.Image src={HomeBannerCreateWithAi} />
              </Styles.InnerCardColumn>

              <Styles.InnerCardColumn>
                <Styles.InnerCardColumnTitle>{homeLocale.v2.banner.listTitle}</Styles.InnerCardColumnTitle>

                <Styles.InnerCardColumnList>
                  {homeLocale?.v2?.banner?.list.map(item => (
                    <Styles.InnerCardColumnListItem key={item.text}>
                      {item.icon}
                      {item.text}
                    </Styles.InnerCardColumnListItem>
                  ))}
                </Styles.InnerCardColumnList>

                <Styles.InnerCardButtonWrapper>
                  <Button
                    size="xlarge"
                    label={homeLocale.v2.banner.cta}
                    onClick={handleCreateSiteWithAi}
                  />
                </Styles.InnerCardButtonWrapper>
              </Styles.InnerCardColumn>
            </Styles.InnerCard>
          )}

          <Services {...servicesProps()} />
        </Styles.WhiteCardContent>
      </Styles.WhiteCard>

      <Styles.Row>
        {isEmptyProducts && (
        <EmptyProductsV2 {...emptyProductsProps()} />
        )}

        <Styles.WhiteCard data-testid="home-get-used-to-cards">
          <Styles.Title>{homeLocale.v2.getUsedToTitle}</Styles.Title>
          <Styles.ItemsRow $flexDirection={isEmptyProducts ? 'column' : 'row'}>
            {homeLocale?.v2?.cardsGetUsedTo.map(item => (
              <Styles.CardItem key={item.id} data-testid="cardItem">
                {item.icon}

                <Styles.CardContent>
                  <Styles.CardTitle>{item.title}</Styles.CardTitle>
                  <Styles.CardDescription>{item.description}</Styles.CardDescription>

                  {!isEmptyProducts && (
                    <Styles.CardCtaWrapper>
                      <Button
                        label={item.cta}
                        variant="text"
                        iconRight={<IconArrow direction="right" color="primaryBlue" />}
                        onClick={() => { handleRedirect({ id: item.id }); }}
                      />
                    </Styles.CardCtaWrapper>
                  )}
                </Styles.CardContent>

                {isEmptyProducts && (
                  <Styles.CardCtaColumn>
                    <Button
                      label={item.cta}
                      variant="text"
                      iconRight={<IconArrow direction="right" color="primaryBlue" />}
                      onClick={() => { handleRedirect({ id: item.id }); }}
                    />
                  </Styles.CardCtaColumn>
                )}
              </Styles.CardItem>
            ))}
          </Styles.ItemsRow>
        </Styles.WhiteCard>
      </Styles.Row>
    </Styles.Wrapper>
  );
};

export default NewHomeLayoutHandler;
